import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ExportButtons } from '@/components/ExportButtons';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ShieldCheck, AlertTriangle, Search, CheckCircle2, HelpCircle } from 'lucide-react';

const STATUS_STYLE = {
  'No issue detected': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Settlement evidence found': 'bg-info/10 text-info border-info/30',
  'Potentially unsettled': 'bg-rose-50 text-rose-700 border-rose-200',
  'Insufficient information — manual review required': 'bg-warning/10 text-warning border-warning/30',
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function OnlineReceivableAuditPage() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ summary: null, rows: [] });

  useEffect(() => {
    api.get('/branches').then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId && branchId !== 'all') params.branch_id = branchId;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.get('/reports/online-receivable-audit', { params });
      setData(res.data || { summary: null, rows: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit');
    } finally {
      setLoading(false);
    }
  }, [branchId, startDate, endDate]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const s = data.summary;
  const exportFilters = {
    branch_id: branchId !== 'all' ? branchId : null,
  };

  const Tile = ({ label, value, color, testid }) => (
    <div className={`p-3 rounded-xl border card-enter ${color}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-lg font-bold inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {fmt(value)}</div>
    </div>
  );

  const BreakdownTable = ({ title, agg }) => (
    <Card className="card-enter">
      <CardHeader className="py-3"><CardTitle className="text-sm font-outfit">{title}</CardTitle></CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left">Key</th>
              <th className="px-3 py-2 text-right">Count</th>
              <th className="px-3 py-2 text-right">Gross</th>
              <th className="px-3 py-2 text-right">Expected Receivable</th>
              <th className="px-3 py-2 text-right">Potential Diff</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(agg || {}).map(([k, v]) => (
              <tr key={k} className="border-t border-border/40">
                <td className="px-3 py-2">{k}</td>
                <td className="px-3 py-2 text-right">{v.count}</td>
                <td className="px-3 py-2 text-right">{fmt(v.gross)}</td>
                <td className="px-3 py-2 text-right">{fmt(v.expected_receivable)}</td>
                <td className="px-3 py-2 text-right">{fmt(v.potential_difference)}</td>
              </tr>
            ))}
            {Object.keys(agg || {}).length === 0 && <tr><td colSpan={5} className="px-3 py-3 text-center text-muted-foreground">No data</td></tr>}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="online-receivable-audit-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold font-outfit flex items-center gap-2"><ShieldCheck size={22} className="text-info" /> Online / Platform Receivable Audit</h1>
            <p className="text-sm text-muted-foreground">Read-only review of historical online & delivery-platform sales under the universal receivable rule. No records are modified.</p>
          </div>
          <ExportButtons branchIds={branchId && branchId !== 'all' ? [branchId] : []} dataType="online-receivable-audit" startDate={startDate} endDate={endDate} filters={exportFilters} />
        </div>

        <Card>
          <CardContent className="flex flex-wrap gap-3 items-end p-4">
            <div><Label className="text-xs">Branch</Label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger className="w-48" data-testid="audit-branch-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">From</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40" data-testid="audit-start-date" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40" data-testid="audit-end-date" /></div>
          </CardContent>
        </Card>

        {s && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Tile label="Historical Gross Sales" value={s.total_gross} color="bg-stone-50 border-border" testid="audit-gross-tile" />
              <Tile label="Currently Treated as Received" value={s.currently_treated_as_received} color="bg-emerald-50 border-emerald-200 text-emerald-700" testid="audit-received-tile" />
              <Tile label="Expected Receivable (New Rule)" value={s.expected_receivable_new_rule} color="bg-rose-50 border-rose-200 text-rose-700" testid="audit-expected-tile" />
              <Tile label="Potential Difference" value={s.potential_difference} color="bg-warning/10 border-warning/30 text-warning" testid="audit-diff-tile" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="p-3 rounded-xl border bg-rose-50 border-rose-200 flex items-center gap-2" data-testid="audit-unsettled-stat">
                <AlertTriangle size={18} className="text-rose-600" />
                <div><div className="text-[10px] uppercase text-rose-600">Potentially Unsettled</div><div className="text-sm font-bold text-rose-700">{s.potentially_unsettled_count} records · <RiyalIcon size="0.7em" /> {fmt(s.potentially_unsettled_amount)}</div></div>
              </div>
              <div className="p-3 rounded-xl border bg-info/10 border-info/30 flex items-center gap-2" data-testid="audit-evidence-stat">
                <CheckCircle2 size={18} className="text-info" />
                <div><div className="text-[10px] uppercase text-info">Settlement Evidence Found</div><div className="text-sm font-bold text-info">{s.settlement_evidence_count} records · <RiyalIcon size="0.7em" /> {fmt(s.settlement_evidence_amount)}</div></div>
              </div>
              <div className="p-3 rounded-xl border bg-warning/10 border-warning/30 flex items-center gap-2" data-testid="audit-manual-stat">
                <HelpCircle size={18} className="text-warning" />
                <div><div className="text-[10px] uppercase text-warning">Manual Review Required</div><div className="text-sm font-bold text-warning">{s.manual_review_count} records · <RiyalIcon size="0.7em" /> {fmt(s.manual_review_amount)}</div></div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">Total records reviewed: <b>{s.total_records}</b> · Online gateway (received): <b>{s.gateway_records}</b> · Delivery platform (receivable): <b>{s.platform_records}</b></div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <BreakdownTable title="By Branch" agg={s.by_branch} />
              <BreakdownTable title="By Delivery Platform" agg={s.by_platform} />
              <BreakdownTable title="By Month" agg={s.by_month} />
              <BreakdownTable title="By Payment Mode" agg={s.by_payment_mode} />
            </div>
          </>
        )}

        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm font-outfit flex items-center gap-2"><Search size={16} /> Sale-level detail ({data.rows.length})</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs" data-testid="audit-detail-table">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-left">Channel</th>
                  <th className="px-2 py-2 text-left">Branch</th>
                  <th className="px-2 py-2 text-left">Platform / Ref</th>
                  <th className="px-2 py-2 text-left">Mode</th>
                  <th className="px-2 py-2 text-right">Gross</th>
                  <th className="px-2 py-2 text-right">Current Rcvbl</th>
                  <th className="px-2 py-2 text-right">Expected Rcvbl</th>
                  <th className="px-2 py-2 text-right">Diff</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-left">Audit</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={11} className="px-3 py-4 text-center text-muted-foreground">Loading…</td></tr>}
                {!loading && data.rows.length === 0 && <tr><td colSpan={11} className="px-3 py-4 text-center text-muted-foreground">No online / platform sales in range</td></tr>}
                {!loading && data.rows.map((r) => (
                  <tr key={r.sale_id} className="border-t border-border/40" data-testid={`audit-row-${r.sale_id}`}>
                    <td className="px-2 py-2">{r.date}</td>
                    <td className="px-2 py-2">{r.channel}</td>
                    <td className="px-2 py-2">{r.branch}</td>
                    <td className="px-2 py-2">{r.platform !== '-' ? r.platform : r.customer_ref}</td>
                    <td className="px-2 py-2">{r.payment_mode}</td>
                    <td className="px-2 py-2 text-right">{fmt(r.gross)}</td>
                    <td className="px-2 py-2 text-right">{fmt(r.current_receivable)}</td>
                    <td className="px-2 py-2 text-right">{fmt(r.expected_receivable_new_rule)}</td>
                    <td className="px-2 py-2 text-right font-semibold">{fmt(r.potential_difference)}</td>
                    <td className="px-2 py-2">{r.payment_status}</td>
                    <td className="px-2 py-2"><Badge variant="outline" className={STATUS_STYLE[r.audit_status] || ''}>{r.audit_status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
